import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";
import { AuthActionCreators, IApplicationState } from "../";
import { IPost } from '../authorization';
import { IBaseResponse } from '../../models';
import { DonorPreselectReviewStatus } from '@nkr/enums/donor-preselect-review-status';
import { DonorPreselectBulkUpdateType } from '@nkr/enums/donor-preselect-bulk-update-type';
import { MortalityMatchType } from '@nkr/enums/mortality-match-type';

export interface IAcceptDonorResponse {
    data: {
        success: boolean,
        message: string,
        results: number
    }
}

export interface IRequestExmResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            xmId: number,
            orderNumber: string
        }
    }
}

export const ActionCreators = {
    acceptDonor: (recipientId: number, donorId: number, isFullyConfident: boolean, hasMortalityRisk: boolean, mortalityMatchType: MortalityMatchType) => async (dispatch: Dispatch<IApplicationState>): Promise<IAcceptDonorResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/accept`,
            data: {
                recipientId,
                donorId,
                isFullyConfident,
                hasMortalityRisk,
                mortalityMatchType
            }
        };

        try {
            const response = await dispatch(AuthActionCreators.apiPost(options));
            return await Promise.resolve(response);
        } catch (error) {
            throw error;
        }
    },
    declineDonor: (recipientId: number, recipientCenterId: number, donorId: number, hardBlockSource: string, declineReasons: { reasonId: number, otherText: string }[], isGlobalDecline: boolean, hasMortalityRisk: boolean, mortalityMatchType: MortalityMatchType) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/decline`,
            data: {
                recipientId,
                recipientCenterId,
                donorId,
                hardBlockSource,
                declineReasons,
                isGlobalDecline,
                hasMortalityRisk,
                mortalityMatchType
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    declineDonorRexm: (recipientId: number, recipientCenterId: number, donorId: number, hardBlockSource: string, hardBlockReason: string, hardBlockComments: string, isGlobalDecline: boolean, hasMortalityRisk: boolean, mortalityMatchType: MortalityMatchType) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/decline-rexm`,
            data: {
                recipientId,
                recipientCenterId,
                donorId,
                hardBlockSource,
                hardBlockReason,
                hardBlockComments,
                isGlobalDecline,
                hasMortalityRisk,
                mortalityMatchType
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    requestInfo: (recipientId: number, donorId: number, message: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/request-info`,
            data: {
                recipientId,
                donorId,
                message
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    requestExm: (recipientId: number, donorId: number, message: string, allowDuplicate: boolean, freshBloodOverride: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IRequestExmResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/request-exm`,
            data: {
                recipientId,
                donorId,
                message,
                allowDuplicate,
                freshBloodOverride
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IRequestExmResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    saveNotes: (recipientCenterId: number, donorId: number, recipientId: number, note: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/note`,
            data: {
                recipientCenterId,
                donorId,
                recipientId,
                note
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    bulkUpdatePreselects: (bulkUpdateType: DonorPreselectBulkUpdateType, recipientId: number, donorList: number[], isAccepted: boolean, isFullyConfident: boolean, hardBlockReason: number, reviewStatus: DonorPreselectReviewStatus) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/bulk-update`,
            data: {
                bulkUpdateType,
                recipientId,
                donorList,
                isAccepted,
                isFullyConfident,
                hardBlockReason,
                reviewStatus
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    updateReviewStatus: (recipientId: number, donorId: number, reviewStatus: DonorPreselectReviewStatus) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/update-review-status`,
            data: {
                recipientId,
                donorId,
                reviewStatus
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    bulkDeclineMultiPreselects: (recipientId: number, donorList: number[], declineReasons: { reasonId: number, otherText: string }[]) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options: IPost = {
            url: `${__API__}/donor-preselect/bulk-decline-multi`,
            data: {
                recipientId,
                donorList,
                declineReasons
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
}

export interface IDonorPreselectState {
}

var initialState: IDonorPreselectState = {

};

const createReducer = (initialState: IDonorPreselectState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
};

export const Reducer = createReducer(initialState, handlers);
